import { useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { useRouteContext } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { gamesOptionsOptions } from "../../lib/competition/games";
import { entriesView } from "../../lib/entries/access";
import { type MedalHistoryData, type MedalTier, type SportHistory, type StrongSport, sportHistory } from "../../lib/entries/medal-history";
import { historyNoc } from "../../lib/entries/strength";
import { MedalIcon } from "../common/medal-icon";

/**
 * Admin-only "form guide" on the NOC entry editors: how the NOC being viewed
 * did in each sport at the last three SEA Games (Wikipedia medal tables,
 * `@seagames/contracts/medal-history.json`). History, never a prediction or a rule.
 *
 * Who sees it is the ACTIVE ROLE (`entriesView` = admin), never a permission.
 * The dataset is a dynamic `import()` behind that check, so it is its own
 * chunk and a NOC session never downloads it. Para games: none (the dataset
 * is the SEA Games').
 */
export function useMedalHistory(gamesId: string, orgCode: string | null | undefined): { data: MedalHistoryData; noc: string } | null {
	const noc = historyNoc(orgCode);
	const data = useMedalHistoryData(gamesId, !!noc);
	return useMemo(() => (data && noc ? { data, noc } : null), [data, noc]);
}

/**
 * The dataset itself, for an admin on the SEA Games (not Para); null for
 * everyone else and while it loads. `when` lets a caller hold the download
 * back (no NOC code yet). The admin check is BEFORE the `import()`, so the
 * chunk is never requested by a NOC or host session.
 */
export function useMedalHistoryData(gamesId: string, when = true): MedalHistoryData | null {
	const { session } = useRouteContext({ from: "/_app" });
	const admin = entriesView(session) === "admin";
	const games = useQuery({ ...gamesOptionsOptions(), enabled: admin });
	const isPara = games.data?.find((g) => g.games_id === gamesId)?.is_para;
	const enabled = admin && when && isPara === false;
	const q = useQuery({
		queryKey: ["entries", "medal-history"],
		queryFn: () => import("@seagames/contracts/medal-history.json").then((m) => m.default as unknown as MedalHistoryData),
		enabled,
		staleTime: Number.POSITIVE_INFINITY,
		gcTime: Number.POSITIVE_INFINITY,
	});
	return enabled ? (q.data ?? null) : null;
}

/** Admin on a non-Para games: the Strength map can be offered (before the dataset is downloaded). */
export function useStrengthAvailable(gamesId: string): boolean {
	const { session } = useRouteContext({ from: "/_app" });
	const admin = entriesView(session) === "admin";
	const games = useQuery({ ...gamesOptionsOptions(), enabled: admin });
	return admin && games.data?.find((g) => g.games_id === gamesId)?.is_para === false;
}

const SHOWN_TIERS: readonly MedalTier[] = ["TOP", "GOLD", "MEDAL"];

/** The tile / rail mark: crown on gold (best NOC), gold ring (won gold), bronze dot (medals). */
export function TierBadge({ history }: { history: SportHistory }) {
	const { t } = useTranslation("entries");
	if (!SHOWN_TIERS.includes(history.tier)) return null;
	const { g, s, b } = history.totals;
	const label = t("history.badgeTitle", { tier: t(`history.tier.${history.tier}`), g, s, b });
	return (
		<span className="ent-tier" data-tier={history.tier} title={label} role="img" aria-label={label}>
			{history.tier === "TOP" && <CrownGlyph />}
		</span>
	);
}

export function CrownGlyph() {
	return (
		<svg viewBox="0 0 16 16" aria-hidden focusable={false}>
			<path d="M2.5 11.5 2 5l3.2 2.6L8 3.5l2.8 4.1L14 5l-.5 6.5z" fill="currentColor" />
		</svg>
	);
}

/**
 * The compact strip on an EOI sport card and the EBN sport header: per Games
 * the NOC's g / s / b (or "not held"), the tier and "Medalled in X of Y".
 * No sources, links or source notes (user, 2026-10-30).
 */
export function HistoryStrip({ data, noc, code, sportName }: { data: MedalHistoryData; noc: string; code: string; sportName: string }) {
	const { t } = useTranslation("entries");
	const { formatNumber: fmt } = useUiStrings();
	const h = sportHistory(data, code, noc);
	return (
		<section className="ent-hist" aria-label={t("history.stripLabel", { sport: sportName })}>
			<header className="ent-hist__head">
				<span className="ent-hist__flag">{t("history.adminOnly")}</span>
				<b className="ent-hist__tier">
					<TierBadge history={h} />
					{t(`history.tier.${h.tier}`)}
				</b>
				{h.contestedIn > 0 && (
					<span className="ent-hist__sub">{t("history.medalledIn", { count: h.contestedIn, x: fmt(h.medalledIn), n: fmt(h.contestedIn) })}</span>
				)}
				{h.rank !== null && h.tier !== "NONE" && <span className="ent-hist__sub">{t("history.rank", { rank: fmt(h.rank) })}</span>}
			</header>
			<ol className="ent-hist__years">
				{h.perGames.map((p) => (
					<li key={p.year} className="ent-hist__year" data-held={p.contested || undefined}>
						<span className="ent-hist__yr">{p.year}</span>
						{!p.contested ? (
							<span className="ent-hist__none">{t("history.notHeld")}</span>
						) : !p.hasData ? (
							<span className="ent-hist__none">{t("history.noTable")}</span>
						) : p.g + p.s + p.b === 0 ? (
							<span className="ent-hist__none">{t("history.noMedal")}</span>
						) : (
							<span className="flex items-center gap-[0.1875rem]">
								{(["g", "s", "b"] as const).map((k) => (
									<span
										key={k}
										className="ent-medal ent-medal--xs"
										data-zero={p[k] === 0 || undefined}
										title={t(`history.medal.${k}`, { count: p[k], n: fmt(p[k]) })}
									>
										<MedalIcon medal={k === "g" ? "gold" : k === "s" ? "silver" : "bronze"} />
										<span className="sr-only">{t(`history.medal.${k}`, { count: p[k], n: fmt(p[k]) })}</span>
										<span aria-hidden>{fmt(p[k])}</span>
									</span>
								))}
							</span>
						)}
					</li>
				))}
			</ol>
		</section>
	);
}

const HINT_CHIPS = 8;

/**
 * Above the EOI grid: the sports this NOC won gold in that it has not picked.
 * A chip only shows its tile (filters cleared, scrolled, highlighted) — it
 * never adds the sport.
 */
export function StrongHint({
	strong,
	nameOf,
	onShow,
}: {
	strong: readonly StrongSport[];
	nameOf: (code: string) => string;
	onShow: (code: string) => void;
}) {
	const { t } = useTranslation("entries");
	const { formatNumber: fmt } = useUiStrings();
	const [all, setAll] = useState(false);
	const shown = all ? strong : strong.slice(0, HINT_CHIPS);
	const more = strong.length - shown.length;
	return (
		<aside className="ent-hist ent-hist--hint" aria-labelledby="ent-hist-hint-h">
			<header className="ent-hist__head">
				<span className="ent-hist__flag">{t("history.adminOnly")}</span>
				<b id="ent-hist-hint-h" className="ent-hist__tier">
					{strong.length > 0 ? t("history.strongTitle") : t("history.strongNone")}
				</b>
			</header>
			{strong.length > 0 && (
				<ul className="ent-hist__chips">
					{shown.map((s) => (
						<li key={s.code}>
							<button type="button" className="ent-hist__chip" data-tier={s.tier} onClick={() => onShow(s.code)} title={t("history.chipShow", { sport: nameOf(s.code) })}>
								{s.tier === "TOP" && (
									<span className="ent-tier" data-tier="TOP" aria-hidden>
										<CrownGlyph />
									</span>
								)}
								{t("history.chip", { count: s.gold, sport: nameOf(s.code), n: fmt(s.gold) })}
							</button>
						</li>
					))}
					{more > 0 && (
						<li>
							<button type="button" className="ent-hist__more" onClick={() => setAll(true)}>
								{t("history.more", { count: more, n: fmt(more) })}
							</button>
						</li>
					)}
				</ul>
			)}
		</aside>
	);
}
